
// This is the general purpose app-wide store.
import { defineStore } from 'pinia'
import bus from "/src/utils/event_bus"
import { useSelectedStore } from "./selected"
import { useSessionStore } from "./session"
import { useCompanyStore } from "./company"
import { useFactoriesStore } from "./factories"
import { useTranslationsStore } from "./translations";


export const useAppStore = defineStore('app', {
  state: () => ({
    app_mode: "production",
    active_loaders: [],  // Loading calls that are running
    bootup: true, // Only at startup is true

    route_name: "",
    connection_lost: false,
    // The QR reader is a dialog over whatever screen is open (DialogQrReader).
    qr_reader_open: false,
  }),
  getters: {
    loading: state => state.active_loaders.length != 0,
    loader_is_active: state => (loaders) => {
      if (Array.isArray(loaders)) {
        return loaders.some(loader => state.active_loaders.includes(loader));
      } else {
        return state.active_loaders.includes(loaders);
      }
    },
    companyWide: () => {
      const selected_store = useSelectedStore()
      return selected_store.pr_selection == "all"
    },
    byDateModeActive: () => {
      const selected_store = useSelectedStore()
      const company_wide = selected_store.pr_selection == "all"
      if (!company_wide) return false

      const company_store = useCompanyStore()
      const by_date = company_store.company?.work_by_date
      if (!by_date) return false

      // by_date active in company and no project selected
      return true
    },
    m_bom: () => {
      const session_store = useSessionStore()
      return session_store.user_object?.auth?.company?.m_bom || false
    },
    m_machines: () => {
      const session_store = useSessionStore()
      return session_store.user_object?.auth?.company?.m_machines || false
    },
    m_prod_management: () => {
      const session_store = useSessionStore()
      return session_store.user_object?.auth?.company?.m_prod_management || false
    },
    t: () => function (key) {
      const translationsStore = useTranslationsStore()
      return translationsStore.translate(key)
    }
  },
  actions: {
    setViewerMode() { this.app_mode = "production" },
    setManagerMode() { this.app_mode = "manage" },
    setLogisticsMode() { this.app_mode = "logistics" },
    setDeliveryMode() { this.app_mode = "delivery" },
    setMode(mode) { this.app_mode = mode },
    pushLoading(load) { this.active_loaders.push(load) },
    // Pops ONE occurrence: the same loader can be pushed by two calls in
    // flight at once (a project switched twice, a manual refresh over the
    // periodic one), and dropping every copy would report "done" while the
    // second call is still running.
    popLoading(load) {
      const i = this.active_loaders.indexOf(load)
      if (i != -1) this.active_loaders.splice(i, 1)
    },
    popLoadingIncludes(load) { this.active_loaders = this.active_loaders.filter(l => !l.includes(load)) },
    endBootup() { this.bootup = false },
    setRouteName(name) { this.route_name = name },
    setConnectionLost(status) { this.connection_lost = status },



    async initialLoad() {
      const company_store = useCompanyStore()
      const factories_store = useFactoriesStore()
      return Promise.all([
        company_store.loadCompany(),
        factories_store.loadProductionLines(),
        factories_store.loadStations(),
        company_store.loadIFCPSs(),
        company_store.loadMultipanelConfigs(),
        company_store.loadContacts(),
      ])
    },
    // `show_loading` decides whether the refresh is announced by the app-wide
    // loading bar. The periodic background refresh passes false: it runs every
    // two minutes on its own and must not look like the app is busy.
    async refreshBaseUserData(show_loading = true) {
      if (show_loading) this.pushLoading("base_user_data")
      const company_store = useCompanyStore()
      const session_store = useSessionStore()
      return Promise.all([
        session_store.getUserObject(),
        company_store.loadProjects(),
        company_store.loadStandardLibraries(),
      ]).then(() => {
        if (this.bootup) {
          // ONLY ON BOOT
          let station = session_store.user_object.auth
            .selected_production_station;
          bus.emit("back_event", {
            event: "change_station",
            meta: { station },
          });
          this.endBootup()
        }
      }).finally(() => { if (show_loading) this.popLoading("base_user_data") })
    },
  }
})
